/**
 * Registry of the mounted user-level UserNotesDoc, for non-React callers.
 *
 * React code should read from `UserNotesDocContext` (via `useDeckDoc` /
 * context consumer). But some call-sites are outside React's reach —
 * `card-notes-cleanup.ts`, `note-generation-store.ts`,
 * `utils.fetchInitialDeck`, etc. — and still need to read the user's Y.Doc.
 *
 * `UserNotesDocProvider`'s connected inner component calls
 * `registerActiveUserNotesDoc()` on mount and clears it on unmount. This
 * is NOT a lifecycle owner (the Y.Doc belongs to the provider) — it's
 * just a lookup table for "what's the current user's notes doc?"
 *
 * The deck-level accessor `getActiveDeckDoc(deckName)` is kept for
 * backwards compatibility with consumers that used to do this lookup. It
 * now simply dereferences `activeUserDoc.deck(deckName)` — cheap, no
 * lifecycle.
 *
 * The companion `activePersonalScope` is the spawn config (party + room)
 * that non-React notes accessors use to peek the per-note registry. It
 * mirrors what the {@link import("./note-doc-spawn-context").NoteDocSpawnProvider}
 * sets in React for personal scope.
 */

import type { UserNotesDoc } from './deck-doc';

let activeUserDoc: UserNotesDoc | null = null;

/**
 * Register the mounted UserNotesDoc. Returns an unregister function —
 * call it from the hook's cleanup phase.
 */
export function registerActiveUserNotesDoc(userDoc: UserNotesDoc): () => void {
  activeUserDoc = userDoc;
  return () => {
    // Only clear if we're still the registered instance. Guards against a
    // stale unmount racing a fresh mount (React StrictMode).
    if (activeUserDoc === userDoc) {
      activeUserDoc = null;
    }
  };
}

/**
 * The currently-mounted user notes doc, or `undefined` if signed out or
 * not yet hydrated.
 */
export function getActiveUserNotesDoc(): UserNotesDoc | undefined {
  return activeUserDoc ?? undefined;
}

// ---------------------------------------------------------------------------
// Active personal-scope spawn config — for non-React `ScopedNotes`
// accessors that need to peek (or in the share-time migration case,
// transiently acquire) per-note Y.Docs from the registry.
// ---------------------------------------------------------------------------

/**
 * Coordinates needed to acquire a personal-scope NoteDoc from outside
 * React. `party` + `room` are enough for `peek` (used by every read in
 * `scoped-deck/notes.ts`); `host` + `getToken` are additionally required
 * for `acquire`, which spins up a fresh y-partyserver provider — that
 * path is used at share time to migrate the personal note's Yjs state
 * into the brand-new SG NoteDoc (see `sharing.ts#migrateNotesToSG`).
 *
 * `host` / `getToken` are optional so existing peek-only callers can
 * keep registering a minimal scope; they're populated by
 * `UserDoConnectionProvider`, which has the same values it hands to
 * `NoteDocSpawnProvider`.
 */
export type PersonalScope = {
  party: string;
  room: string;
  host?: string;
  getToken?: () => Promise<string | null>;
};

let activePersonalScope: PersonalScope | null = null;

/**
 * Mark the personal-scope connection as active. Returns an unregister
 * function. Mirrors the React `NoteDocSpawnProvider` value for personal
 * scope, so non-React `ScopedNotes` accessors can find the right room
 * when peeking the per-note registry.
 */
export function registerActivePersonalScope(scope: PersonalScope): () => void {
  activePersonalScope = scope;
  return () => {
    // The clear is DEFERRED to a microtask, and skipped when the scope was
    // registered again meanwhile. The provider re-registers whenever its
    // spawn config's `getToken` changes identity, and React runs that
    // cleanup BEFORE the same commit's child effects and the re-register
    // AFTER them — so a synchronous clear left every child effect of that
    // commit with no scope. NotesView's auto-create of a brand-new note's
    // fragment is one: it ran in that gap, `notes.fragment()` found no doc,
    // nothing re-ran it, and "Write notes" on an empty deck opened a blank
    // pane that no keystroke could reach until a reload (PR #752 board,
    // found by `deck-empty-state-web`).
    queueMicrotask(() => {
      if (activePersonalScope === scope) activePersonalScope = null;
    });
  };
}

/**
 * Return the current personal-scope spawn config, or `null` if the
 * user-do connection isn't mounted.
 */
export function getActivePersonalScope(): PersonalScope | null {
  return activePersonalScope;
}
